import { getDisplayPhoneNumber, type DisplayContact } from '@aikoa/common'
import { type PropsWithChildren } from 'react'
import { View } from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { Heading3 } from '~/components/typography/Titles'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

type ContactRowProps = {
  contact: DisplayContact
  className?: string
}

export function ContactRowCompact({ contact, className }: ContactRowProps) {
  return (
    <View className={cn('pointer-events-none flex-1 flex-row items-center gap-3 px-1', className)}>
      <View className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        <Icon name="account-outline" size={18} className="text-secondary" />
      </View>
      <Heading3 className="text-base leading-none" numberOfLines={1}>
        {contact.name}
      </Heading3>
    </View>
  )
}

export function ContactRow({ contact, children, className }: PropsWithChildren<ContactRowProps>) {
  return (
    <Animated.View
      entering={FadeIn.duration(150)}
      exiting={FadeOut.duration(100)}
      className={cn('gap-3 rounded-xl border border-primary/30 bg-background p-4', className)}>
      <View className="flex-1 flex-row items-center gap-1">
        <View className="flex-1 flex-row items-center gap-3">
          <View className="relative -ml-1 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
            <Icon name="user" size={16} className="text-secondary" />
          </View>
          <Text
            className="font-medium text-base/normal text-primary"
            numberOfLines={1}
            ellipsizeMode="tail">
            {contact.name}
          </Text>
        </View>
        {(contact.phone || contact.email) && (
          <View className="justify-baseline shrink-0 items-end gap-0.5">
            {contact.phone && (
              <Text
                className="text-sm/normal text-secondary"
                numberOfLines={1}
                ellipsizeMode="middle">
                {getDisplayPhoneNumber(contact.phone)}
              </Text>
            )}
            {contact.email && (
              <Text className="text-sm/normal text-secondary" numberOfLines={1}>
                {contact.email}
              </Text>
            )}
          </View>
        )}
      </View>
      {children}
    </Animated.View>
  )
}
